import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { ClubApi } from "../../services/clubApi";
import { getClubJoinOption, launchClubCheckout } from "../../utils/clubJoin";
import { devError, devLog, devWarn } from "../../utils/devLog";

const ClubJoinCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const fastSpringStorefront =
    document.getElementById("fsc-api")?.getAttribute("data-storefront") || "";
  const [status, setStatus] = useState("Preparing your membership card...");
  const [error, setError] = useState("");
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [lastLaunchMethod, setLastLaunchMethod] = useState<"builder" | "url" | "none" | null>(
    null
  );
  const autoLaunchAttemptedRef = useRef(false);
  const hasRequiredNameFields = Boolean(
    String(user?.firstName || "").trim() && String(user?.lastName || "").trim()
  );

  const planId = searchParams.get("plan");
  const option = useMemo(() => getClubJoinOption(planId), [planId]);
  const checkoutBenefits = useMemo(
    () => [
      "Monthly premium case files",
      "Weekly Shroud of Darkness archive briefings",
      "Rank progression across active service",
      "Guest case sharing for active members",
    ],
    []
  );

  useEffect(() => {
    devLog("route", "club_join_checkout_route_rendered", {
      route: "/agency/join",
      planId,
      hasUser: Boolean(user),
      hasRequiredNameFields,
      builderAvailable: Boolean(window.fastspring?.builder),
      storefront: fastSpringStorefront,
    });
  }, [fastSpringStorefront, hasRequiredNameFields, planId, user]);

  const handleLaunchCheckout = (source: string) => {
    if (!option) return;

    setError("");
    setIsLaunching(true);
    setStatus(`Opening ${option.name} checkout...`);

    devLog("route", "club_join_checkout_launch_requested", {
      route: "/agency/join",
      source,
      planId: option.id,
      productPath: option.productPath,
      builderAvailable: Boolean(window.fastspring?.builder),
      checkoutUrlConfigured: Boolean(option.checkoutUrl),
      storefront: fastSpringStorefront,
    });

    const checkout = launchClubCheckout(option);
    setLastLaunchMethod(checkout.method);

    if (checkout.launched) {
      setStatus(
        checkout.method === "builder"
          ? `Opening ${option.name} in secure popup checkout...`
          : `Redirecting to ${option.name} checkout...`
      );
      devLog("route", "club_join_checkout_launch_succeeded", {
        route: "/agency/join",
        source,
        planId: option.id,
        method: checkout.method,
        storefront: fastSpringStorefront,
      });
    } else {
      setError(
        "FastSpring checkout is not ready. Confirm the storefront script, product path, and popup domain whitelist."
      );
      setStatus("Checkout is not ready yet.");
      devWarn("route", "club_join_checkout_launch_unavailable", {
        route: "/agency/join",
        source,
        planId: option.id,
        builderAvailable: Boolean(window.fastspring?.builder),
        checkoutUrlConfigured: Boolean(option.checkoutUrl),
        storefront: fastSpringStorefront,
      });
    }

    setIsLaunching(false);
  };

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!user) {
        setIsBootstrapping(false);
        return;
      }
      if (!option) {
        navigate("/agency", { replace: true });
        return;
      }
      if (!hasRequiredNameFields) {
        setError(
          "First and last name are required in your Clerk profile before starting checkout."
        );
        setIsBootstrapping(false);
        return;
      }

      try {
        setIsBootstrapping(true);
        setStatus("Creating your membership record...");
        devLog("api", "club_join_checkout_bootstrap_started", {
          route: "/agency/join",
          planId: option.id,
          email: user?.primaryEmailAddress?.emailAddress || "",
        });
        await ClubApi.bootstrapMe({
          email: user?.primaryEmailAddress?.emailAddress || "",
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          displayName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        });

        if (!isMounted) return;
        setIsReady(true);
        setStatus("Your member record is ready. Opening secure checkout...");
        devLog("api", "club_join_checkout_bootstrap_succeeded", {
          route: "/agency/join",
          planId: option.id,
          email: user?.primaryEmailAddress?.emailAddress || "",
        });
        if (!autoLaunchAttemptedRef.current) {
          autoLaunchAttemptedRef.current = true;
          handleLaunchCheckout("auto_after_bootstrap");
        }
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || "Unable to launch checkout right now.");
        setStatus("We could not prepare your membership checkout.");
        devError("api", "club_join_checkout_bootstrap_failed", {
          route: "/agency/join",
          planId: option.id,
          message: err?.response?.data?.message || err?.message || "unknown_error",
        });
      } finally {
        if (isMounted) {
          setIsBootstrapping(false);
        }
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [
    hasRequiredNameFields,
    navigate,
    option,
    user,
    user?.firstName,
    user?.lastName,
    user?.primaryEmailAddress?.emailAddress,
  ]);

  if (!option) return null;

  return (
    <section className="mx-auto max-w-5xl px-4 pb-10 pt-6 text-white">
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <article className="rounded-[2rem] border border-amber-300/30 bg-[radial-gradient(circle_at_80%_0%,rgba(153,88,33,0.26),transparent_38%),radial-gradient(circle_at_10%_0%,rgba(45,66,112,0.24),transparent_42%),linear-gradient(145deg,#090d15,#141014)] p-7 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
          <p className="text-xs uppercase tracking-[0.28em] text-amber-200">Agency Membership</p>
          <div className="mt-5 flex flex-wrap items-end gap-4">
            <div>
              <h1 className="text-3xl font-black leading-tight md:text-5xl">
                Digital Agency Membership
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-300 md:text-base">
                A premium detective membership with monthly case files, weekly archive
                briefings, rank progression, and guest case sharing for active members.
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/20 bg-amber-200/10 px-5 py-4">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-100/80">Membership</p>
              <p className="mt-1 text-4xl font-black">$6</p>
              <p className="text-sm text-amber-50/85">per month</p>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-full border border-amber-200/20 bg-black/25 p-1">
            <div className="h-2 animate-pulse rounded-full bg-gradient-to-r from-amber-300 via-amber-100 to-amber-300" />
          </div>

          <div className="mt-4 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="text-xs uppercase tracking-[0.18em] text-amber-100/80">
              {isBootstrapping
                ? "Preparing member record"
                : isLaunching
                  ? "Connecting checkout"
                  : isReady
                    ? "Checkout ready"
                    : "Membership setup required"}
            </span>
          </div>

          <p className="mt-4 text-sm text-zinc-200">{status}</p>

          {error && (
            <p className="mt-4 rounded-2xl border border-red-400/35 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => handleLaunchCheckout("join_card_primary")}
              disabled={isBootstrapping || isLaunching || !hasRequiredNameFields}
              className="rounded-full bg-amber-400 px-6 py-3 text-sm font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBootstrapping ? "Preparing Checkout..." : "Open Secure Checkout"}
            </button>
            <button
              type="button"
              onClick={() => handleLaunchCheckout("join_card_retry")}
              disabled={isBootstrapping || isLaunching || !hasRequiredNameFields}
              className="rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Retry Checkout
            </button>
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300">
            <p className="font-semibold text-white">If checkout does not appear:</p>
            <p className="mt-2 leading-relaxed">
              Confirm the FastSpring popup is allowed, the configured storefront is correct,
              and your local domain is whitelisted in FastSpring popup checkout settings.
            </p>
          </div>
        </article>

        <aside className="rounded-[2rem] border border-[#232a38] bg-[#0a0f18] p-6 shadow-[0_24px_60px_rgba(0,0,0,0.35)]">
          <p className="text-xs uppercase tracking-[0.28em] text-[#c6b894]">What Unlocks</p>
          <ul className="mt-5 space-y-3">
            {checkoutBenefits.map((benefit) => (
              <li
                key={benefit}
                className="rounded-2xl border border-[#232a38] bg-[#111722] px-4 py-3 text-sm text-[#eceae4]"
              >
                {benefit}
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-2xl border border-cyan-300/20 bg-cyan-400/10 p-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-cyan-100">Checkout Route</p>
            <p className="mt-2 text-sm leading-relaxed text-cyan-50/90">
              {lastLaunchMethod === "builder"
                ? "FastSpring popup checkout was requested from the builder script."
                : lastLaunchMethod === "url"
                  ? "FastSpring redirect checkout was requested from the fallback URL."
                  : "This screen prepares your member record, then opens FastSpring checkout."}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/agency"
              className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Back to Plans
            </Link>
            <Link
              to="/agency/dashboard"
              className="rounded-full border border-amber-200/30 bg-amber-200/10 px-5 py-2.5 text-sm font-semibold text-amber-50 transition hover:bg-amber-200/15"
            >
              Go to Dashboard
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default ClubJoinCheckoutPage;
